import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { tonePillCls, type CommandStatus } from "@/lib/parent/commandMetrics";
import InfoTip from "./InfoTip";

interface ChildOpt { user_id: string; display_name: string; age?: number | null; }

interface Props {
  children: ChildOpt[];
  selectedChildId: string;
  onSelectChild: (id: string) => void;
  displayName: string;
  ageLabel?: string | null;
  status: CommandStatus;
}

export default function CommandHeader({ children, selectedChildId, onSelectChild, displayName, ageLabel, status }: Props) {
  return (
    <header className="relative overflow-hidden rounded-2xl border border-white/[0.07] ring-1 ring-inset ring-white/[0.03] bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.5)_100%)] pl-14 pr-3 py-3 backdrop-blur-2xl shadow-[0_24px_60px_-36px_rgba(0,0,0,0.7),inset_0_1px_0_0_hsl(var(--foreground)/0.06)] sm:pl-5 sm:pr-5 sm:py-4">
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />
      <div className="pointer-events-none absolute -top-16 right-1/4 h-40 w-40 rounded-full bg-cyan-500/[0.05] blur-[80px]" />
      <div className="relative flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(34,211,238,0.18),rgba(167,139,250,0.14))] text-xs font-semibold text-foreground ring-1 ring-white/[0.08]">
              {initials(displayName)}
            </span>
            <div className="min-w-0">
              <p className="text-[9px] font-medium uppercase tracking-[0.22em] text-cyan-300/80 leading-none">Family Command Center</p>
              {children.length > 1 ? (
                <select
                  value={selectedChildId}
                  onChange={(e) => onSelectChild(e.target.value)}
                  className="-ml-1 mt-0.5 rounded-md bg-transparent px-1 py-0.5 font-heading text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 sm:text-base"
                  aria-label="Select child"
                >
                  {children.map((c) => (
                    <option key={c.user_id} value={c.user_id}>{c.display_name}</option>
                  ))}
                </select>
              ) : (
                <p className="mt-0.5 font-heading text-sm font-semibold tracking-tight text-foreground sm:text-base">{displayName}</p>
              )}
              {ageLabel && <p className="text-[10px] text-muted-foreground leading-none">{ageLabel}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap", tonePillCls(status.tone))}>
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
              {status.label}
            </span>
            <InfoTip
              title={status.label}
              body="HeirWise sets this from recent lessons, quizzes, and active days. It updates as your teen learns."
              nextStep={status.state === "reengage" ? "A short 15-minute session can rebuild momentum." : status.state === "empty" ? "Open the first lesson together to start tracking." : "Keep the cadence going."}
              align="end"
            />
          </div>
      </div>
    </header>
  );
}

function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}